import { PatientFileVersion } from "../../../amplify/appsync/query";
import { PatientVisit } from "../../../amplify/appsync/search-patients";

export function chunkArray(arr: any, size: number = 3) {
    const chunks: any = [];
    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
}

export const getChunkKey = (chunks: any[]) => {
    let chunkKey = "";
    for (let study of chunks) {
        chunkKey += `${study.study}${study.result_time}`;
    }
    return chunkKey;
};

export const truncateString = (str: string, maxLength = 500) => {
  return str.length > maxLength ? str.slice(0, maxLength) : str;
}

export const isSectionUnread = (key: string, unreadFileVersionMap?: Record<string, PatientFileVersion>) => {
    return unreadFileVersionMap ? unreadFileVersionMap[key] : false;
}

export const processTextForCopy = (str: string, anchorDate: Date) => {
    let text = str;

    // Remove citations in square brackets FIRST (before timestamp removal)
    // This simplifies timestamp patterns by removing adjacent citations
    text = text.replace(/\[\s*\d+(?:\s*[,\-–—]\s*\d+)*\s*\]/g, '');

    // Remove parenthesized timestamps (both absolute and relative) from copy-paste version
    // These are supplementary metadata that become stale or cluttered when copy-pasted
    // Preserve timestamps in prose (e.g., "pain started 3 hours ago")
    //
    // Pattern 1: "(3 hours ago)" - parenthesized relative timestamps
    text = text.replace(/\(\d+\s+(hours?|days?|weeks?|minutes?|seconds?|months?|years?)\s+ago\)/gi, '');

    // Pattern 2: "(yesterday)" or "(today)" - parenthesized relative day references
    text = text.replace(/\((yesterday|today)\)/gi, '');

    // Pattern 3: "(2025-12-14)" or "(2025-12-14 10:30)" or "(2025-12-14 10:30:00 PT)" - parenthesized absolute timestamps
    text = text.replace(/\(\d{4}-\d{2}-\d{2}(?:\s+\d{2}:\d{2}(?::\d{2})?)?(?:\s+P[SD]?T)?\)/gi, '');

    // Pattern 4: "(3 hours ago, 2025-12-14 10:30 PT)" - both timestamps in parentheses
    text = text.replace(/\(\d+\s+(?:hours?|days?|weeks?|minutes?|seconds?|months?|years?)\s+ago,\s*\d{4}-\d{2}-\d{2}[^)]*\)/gi, '');

    // Pattern 5: Timestamps inside parentheticals - use lookahead for closing paren
    // e.g., "(SVT, rate 130+; 2025-12-15 07:52 PT)" → "(SVT, rate 130+)"
    // Allow commas/spaces between timestamp and closing paren (left over from citation removal)
    text = text.replace(/[;,]\s*\d{4}-\d{2}-\d{2}(?:\s+\d{2}:\d{2}(?::\d{2})?)?(?:\s+P[SD]?T)?[,\s]*(?=\))/gi, '');

    // Pattern 6: Parentheticals that contain ONLY a timestamp (possibly with trailing space)
    // e.g., "(2025-12-15 08:19 PT )" → ""
    text = text.replace(/\(\s*\d{4}-\d{2}-\d{2}(?:\s+\d{2}:\d{2}(?::\d{2})?)?(?:\s+P[SD]?T)?\s*\)/gi, '');

    // Clean up empty or near-empty parentheses left after removal
    text = text.replace(/\(\s*\)/g, '');

    // Clean up trailing punctuation/space inside parentheses: "(text, )" → "(text)"
    text = text.replace(/[;,]\s*\)/g, ')');
    text = text.replace(/\(\s*[;,]\s*/g, '(');
    text = text.replace(/\s+\)/g, ')');

    // Remove multiple consecutive commas (including ones left after citations)
    text = text.replace(/,+/g, ',')       // collapse multiple commas
            .replace(/,\s*,/g, ',')   // remove comma followed by comma
            .replace(/\s*,\s*\./g, '.') // remove comma before period
            .replace(/^\s*,|,\s*$/g, '') // leading/trailing commas
            .replace(/\s{2,}/g, ' ');   // extra spaces

    // Remove spaces before punctuation (.,!?:;)
    text = text.replace(/\s+([.,!?:;])/g, '$1');

    // Remove extra spaces caused by removal
    text = text.replace(/\s{2,}/g, ' ').trim();

    return text;
};

export const isLobby = (patientVisit: PatientVisit) => {
    const bed = patientVisit.bed;
    if (!bed) {
        return false;
    }
    return bed.startsWith("EDW") || bed.startsWith("Waiting") || bed.startsWith("LDC");
}
